import React, { useContext, useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  useMediaQuery,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  ListItemIcon,
  Collapse,
  Menu,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import LanguageIcon from '@mui/icons-material/Language';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import ArticleIcon from '@mui/icons-material/Article';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useLocation } from 'react-router-dom';
import logo from '../../assets/img/logo_v3.png';
import EntityContext from '../../context/EntityContext';

const iconMap: { [key: string]: React.ReactElement } = {
  LanguageIcon: <LanguageIcon />,
  AccountBalanceWalletIcon: <AccountBalanceWalletIcon />,
  NewspaperIcon: <NewspaperIcon />,
  ArticleIcon: <ArticleIcon />,
  AttachMoneyIcon: <AttachMoneyIcon />,
  MoneyOffIcon: <MoneyOffIcon />,
  PeopleAltIcon: <PeopleAltIcon />,
  CardTravelIcon: <CardTravelIcon />,
  MoreHorizIcon: <MoreHorizIcon />,
};

interface MenuItemData { // Renomeado para evitar conflito com o componente MenuItem do MUI
  title: string;
  url?: string;
  icon?: string;
  subItems?: MenuItemData[];
}

// Componente para lidar com menus que têm sub-itens
const SubMenu: React.FC<{ item: MenuItemData }> = ({ item }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleItemClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  return (
    <>
      <Button
        color="inherit"
        onClick={handleClick}
        startIcon={item.icon ? iconMap[item.icon] : undefined}
        sx={{ mx: 2 }}
      >
        {item.title}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {item.subItems?.map((subItem) => (
          <MenuItem
            key={subItem.title}
            onClick={() => subItem.url && handleItemClick(subItem.url)}
            disabled={!subItem.url}
          >
            <ListItemIcon>{subItem.icon && iconMap[subItem.icon]}</ListItemIcon>
            {subItem.title}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};


const CustomAppBar: React.FC = () => {
  const location = useLocation();
  const { entityId, entityName, entitySeal, entityType } = useContext(EntityContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItemData[]>([]);

  useEffect(() => {
    if (entityId) {
      const fetchMenuItems = async () => {
        try {
          // Tenta carregar o JSON específico da entidade
          let response = await fetch(`/configs/entities/${entityId}.json`);
          // Se não encontrar, tenta carregar o default.json
          if (!response.ok) {
            response = await fetch(`/configs/entities/default.json`);
          }

          if (response.ok) {
            const data = await response.json();
            setMenuItems(data);
          } else {
            console.error('Failed to load any menu config');
            setMenuItems([]); // Fallback para menu vazio
          }
        } catch (error) {
          console.error('Error fetching menu config:', error);
          setMenuItems([]); // Fallback para menu vazio
        }
      };

      fetchMenuItems();
    }
  }, [entityId]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setDrawerOpen(open);
    };

  const isEntityPage = location.pathname.startsWith('/entidade');

  const handleSubMenuClick = () => {
    setOpenSubMenu(!openSubMenu);
  };

  const drawerList = (
    <Box sx={{ width: 250 }} role="presentation">
      <List>
        <ListItem>
          <Typography variant="h6">{entityName || 'Menu'}</Typography>
        </ListItem>
        <Divider />
        {menuItems.map((item) =>
          item.subItems ? (
            <React.Fragment key={item.title}>
              <ListItemButton onClick={handleSubMenuClick}>
                <ListItemIcon sx={{ color: 'inherit' }}>
                  {item.icon && iconMap[item.icon]}
                </ListItemIcon>
                <ListItemText primary={item.title} />
                {openSubMenu ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
              <Collapse in={openSubMenu} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItemButton
                      key={subItem.title}
                      sx={{ pl: 4 }}
                      onClick={toggleDrawer(false)}
                      {...(subItem.url
                        ? {
                            href: subItem.url,
                            target: '_blank',
                            rel: 'noopener noreferrer',
                          }
                        : {
                            disabled: true,
                          })}
                    >
                      <ListItemIcon sx={{ color: 'inherit' }}>
                        {subItem.icon && iconMap[subItem.icon]}
                      </ListItemIcon>
                      <ListItemText primary={subItem.title} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ) : (
            <ListItem key={item.title} disablePadding>
              <ListItemButton
                onClick={toggleDrawer(false)}
                {...(item.url
                  ? {
                      href: item.url,
                      target: '_blank',
                      rel: 'noopener noreferrer',
                    }
                  : {
                      disabled: true,
                    })}
              >
                <ListItemIcon sx={{ color: 'inherit' }}>
                  {item.icon && iconMap[item.icon]}
                </ListItemIcon>
                <ListItemText primary={item.title} />
              </ListItemButton>
            </ListItem>
          )
        )}
      </List>
    </Box>
  );

  return (
    <AppBar position="static" color="primary">
      {isEntityPage ? (
        isMobile ? (
          <Toolbar sx={{ py: 2 }}>
            {entitySeal && (
              <Box
                component="img"
                src={`data:image/png;base64,${entitySeal}`}
                alt="Brasão da Entidade"
                sx={{
                  height: 56,
                  width: 'auto',
                  mr: 2,
                  alignSelf: 'center',
                }}
              />
            )}
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography variant="h6" component="div">
                {entityName || 'Carregando...'}
              </Typography>
              {entityType && (
                <Chip
                  label={entityType}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    mt: 0.5,
                    alignSelf: 'flex-start',
                  }}
                />
              )}
            </Box>

            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={toggleDrawer(false)}
            >
              {drawerList}
            </Drawer>
          </Toolbar>
        ) : (
          // Desktop View
          <Toolbar
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: 2,
              pb: 0.5,
            }}
          >
            {/* Top Row: Entity Info */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {entitySeal && (
                <Box
                  component="img"
                  src={`data:image/png;base64,${entitySeal}`}
                  alt="Brasão da Entidade"
                  sx={{
                    height: 56,
                    width: 'auto',
                    mr: 2,
                    alignSelf: 'center',
                  }}
                />
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h6" component="div">
                  {entityName || 'Carregando...'}
                </Typography>
                {entityType && (
                  <Chip
                    label={entityType}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      mt: 0.5,
                      alignSelf: 'flex-start',
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Bottom Row: Menu */}
            <Box sx={{ pt: 1 }}>
              {menuItems.map((item) =>
                item.subItems ? (
                  <SubMenu key={item.title} item={item} />
                ) : (
                  <Button
                    key={item.title}
                    color="inherit"
                    {...(item.url
                      ? {
                          href: item.url,
                          target: '_blank',
                          rel: 'noopener noreferrer',
                        }
                      : {
                          disabled: true,
                        })}
                    startIcon={item.icon ? iconMap[item.icon] : undefined}
                    sx={{ mx: 2 }}
                  >
                    {item.title}
                  </Button>
                )
              )}
            </Box>
          </Toolbar>
        )
      ) : (
        // Home Page View
        <Toolbar sx={{ py: 2, justifyContent: 'center' }}>
          <Box
            component="img"
            src={logo}
            alt="Logo"
            sx={{
              height: 50,
              width: 'auto',
            }}
          />
        </Toolbar>
      )}
    </AppBar>
  );
};

export default CustomAppBar;
